import { useState, lazy, Suspense } from "react";
import { BookOpen, ArrowDown } from "lucide-react";
import SaintVideoSection from "@/components/SaintVideoSection";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import AuthorityChain from "@/components/sections/AuthorityChain";
import KeyTakeaways from "@/components/sections/KeyTakeaways";
import Navbar from "@/components/Navbar";
import BookingModal from "@/components/BookingModal";
import SaintQuizPopup from "@/components/SaintQuizPopup";
import SEOHead from "@/components/SEOHead";
import { CLINIC_REF } from "@/components/schemaConstants";
import { SaintWhoItsFor } from "@/pages/saint/sections/SaintWhoItsFor";

/** Page-level JSON-LD for /saint (MedicalTherapy + BreadcrumbList + FAQPage).
 * Emitted server-side from the route's `head().scripts` so it lands in <head>,
 * and re-exported so the route can consume it without duplicating the data. */
export const saintSchema: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "@id": "https://neurowellnesstms.com/saint#therapy",
    name: "SAINT® Accelerated TMS",
    description:
      "FDA-cleared, fMRI-guided 5-day accelerated transcranial magnetic stimulation for adults with major depressive disorder who have not achieved adequate relief from antidepressants.",
    url: "https://neurowellnesstms.com/saint",
    provider: CLINIC_REF,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://neurowellnesstms.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "SAINT® TMS",
        item: "https://neurowellnesstms.com/saint",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How does the initial consultation work? Is it in person or virtual?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Your initial consultation is conducted conveniently over a Zoom web conference call with our clinical care team. As your steps evolve, you will also have the option to meet with our psychiatrist either by Zoom web conference or in person at our clinic. The only mandatory in-person steps are the resting-state fMRI brain mapping and the five-day treatment series itself.",
        },
      },
      {
        "@type": "Question",
        name: "Which steps of SAINT treatment must be completed in person?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The two mandatory in-person steps are: (1) the resting-state fMRI brain scan (about 45 minutes at an imaging partner facility), and (2) the five-day treatment week at our Coral Springs clinic (ten 10-minute sessions per day with 50-minute breaks in our private lounge suites). Initial consultations and ongoing psychiatric evaluations can be done via Zoom web conference or in person.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need my own fMRI first?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. We order and coordinate the resting-state fMRI scan for you. You do not need to arrange or bring your own scan.",
        },
      },
      {
        "@type": "Question",
        name: "Do I have to stop my medications?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Usually no. Do not change or stop any medication without first discussing it with your prescribing doctor.",
        },
      },
      {
        "@type": "Question",
        name: "What does SAINT TMS feel like?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most people feel a tapping sensation and hear a clicking sound from the magnetic coil. There is no sedation and you remain awake and alert throughout.",
        },
      },
      {
        "@type": "Question",
        name: "Can I drive myself to and from treatment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. SAINT is non-invasive and does not require sedation, so you can drive yourself to and from each treatment day.",
        },
      },
      {
        "@type": "Question",
        name: "What happens if I don't reach remission by day five?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Your care team and psychiatrist will discuss follow-up options and any appropriate next steps based on your individual response to treatment, available either in person or via Zoom.",
        },
      },
      {
        "@type": "Question",
        name: "How do I access SAINT® — insurance or self-pay?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "There are two pathways. We accept Blue Cross Blue Shield in select states and the Blue Cross Blue Shield Federal Employee Program in select states; because insurance requires a review and approval process, this pathway generally takes longer from consultation to treatment. Our most common option is self-pay, which offers the greatest speed and flexibility — most patients move from consultation to treatment within a few days, with appointments available seven days a week, extended hours, and private treatment suites. Coverage varies by state and plan and is not guaranteed. Contact us to determine eligibility and the fastest path to care for your situation.",
        },
      },
      {
        "@type": "Question",
        name: "Is SAINT the same as accelerated TMS at other clinics?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Only Verified SAINT Providers use the FDA-cleared SAINT Neuromodulation System with fMRI-guided targeting. You can view our official certification and verified provider details at /find-a-verified-saint-provider.",
        },
      },
      {
        "@type": "Question",
        name: "What is the FDA status of SAINT®?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SAINT® received FDA Breakthrough Device Designation and was cleared under 510(k) (K220177) in 2022 for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. It is the only FDA-cleared depression treatment that uses your own fMRI brain imaging to personalize the stimulation target.",
        },
      },
      {
        "@type": "Question",
        name: "Do the results last beyond the treatment week?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Published research includes a study demonstrating sustained remission at 12 months when SAINT® is paired with a personalized continuation and maintenance therapy plan. Your care team builds an individualized follow-up strategy to help protect the progress you make during your five-day treatment week.",
        },
      },
    ],
  },
];

// Above-the-fold — eager so the hero + first scroll content hydrates
// immediately and the booking/quiz modal state is interactive at once.
import Footer from "@/components/sections/Footer";
import { SaintHero } from "@/pages/saint/sections/SaintHero";
import { SaintEligibilityCheck } from "@/components/SaintEligibilityCheck";
import { FreeBenefitsCheck } from "@/components/FreeBenefitsCheck";

// Below-fold sections — lazy-loaded so their JS (Radix accordion, embla
// carousel, react-hook-form, large content blocks, facility photos) ships
// in separate chunks that load after hydration, not in the initial page
// chunk. TanStack Start's streaming SSR still renders their content for
// crawlers/AI; the client only pays the parse/execute cost after paint.
const TmsTherapy = lazy(() => import("@/components/sections/TmsTherapy"));
const BrighterTomorrowBanner = lazy(
  () => import("@/components/sections/BrighterTomorrowBanner"),
);
const Consultation = lazy(() => import("@/components/sections/Consultation"));
const TwoWaysToGetStarted = lazy(
  () => import("@/components/sections/TwoWaysToGetStarted"),
);
const RelatedPages = lazy(() =>
  import("@/components/RelatedPages").then((m) => ({
    default: m.RelatedPages,
  })),
);
const SaintTargetingComparisonSection = lazy(() =>
  import("@/pages/saint/sections/SaintTargetingComparisonSection").then(
    (m) => ({ default: m.SaintTargetingComparisonSection }),
  ),
);
const SaintOutcomes = lazy(() =>
  import("@/pages/saint/sections/SaintOutcomes").then((m) => ({
    default: m.SaintOutcomes,
  })),
);
const SaintWeek = lazy(() =>
  import("@/pages/saint/sections/SaintWeek").then((m) => ({
    default: m.SaintWeek,
  })),
);
const SaintCandidacy = lazy(() =>
  import("@/pages/saint/sections/SaintCandidacy").then((m) => ({
    default: m.SaintCandidacy,
  })),
);
const SaintCost = lazy(() =>
  import("@/pages/saint/sections/SaintCost").then((m) => ({
    default: m.SaintCost,
  })),
);
const SaintStories = lazy(() =>
  import("@/pages/saint/sections/SaintStories").then((m) => ({
    default: m.SaintStories,
  })),
);
const SaintWhyUs = lazy(() =>
  import("@/pages/saint/sections/SaintWhyUs").then((m) => ({
    default: m.SaintWhyUs,
  })),
);
const SaintTravelTeaser = lazy(() =>
  import("@/pages/saint/sections/SaintTravelTeaser").then((m) => ({
    default: m.SaintTravelTeaser,
  })),
);
const SaintPrivateSuites = lazy(() =>
  import("@/pages/saint/sections/SaintPrivateSuites").then((m) => ({
    default: m.SaintPrivateSuites,
  })),
);
const SaintFaq = lazy(() =>
  import("@/pages/saint/sections/SaintFaq").then((m) => ({
    default: m.SaintFaq,
  })),
);
const SaintConsultationCta = lazy(() =>
  import("@/pages/saint/sections/SaintConsultationCta").then((m) => ({
    default: m.SaintConsultationCta,
  })),
);
const SaintArticlesSection = lazy(() =>
  import("@/pages/saint/sections/SaintArticlesSection").then((m) => ({
    default: m.SaintArticlesSection,
  })),
);
const SaintDisclaimer = lazy(() =>
  import("@/pages/saint/sections/SaintDisclaimer").then((m) => ({
    default: m.SaintDisclaimer,
  })),
);
const CrisisLifelineBanner = lazy(
  () => import("@/components/CrisisLifelineBanner"),
);

const Saint = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="SAINT® TMS in Coral Springs, FL | Verified SAINT Provider"
        description="FDA-cleared 5-day fMRI-guided SAINT® TMS for treatment-resistant depression in Coral Springs, FL. Self-pay from $25,000; free insurance benefits check. Individual results vary."
        canonical="/saint"
        keywords="SAINT TMS Coral Springs, accelerated TMS therapy, Stanford neuromodulation therapy, treatment-resistant depression South Florida, 5-day TMS protocol, FDA-cleared depression treatment, Verified SAINT Provider"
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* 1. Hero — what SAINT is, in one glance */}
        <SaintHero />

        {/* 1. Who SAINT is for — the types of patients we care for */}
        <SaintWhoItsFor />

        {/* 1a. Featured patient journey film */}
        <section className="bg-background py-10 sm:py-14">
          <div className="container-px mx-auto max-w-4xl px-4">
            <div className="mb-6 text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-accent">
                Watch &amp; Learn
              </span>
              <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
                The SAINT® Patient Journey
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-base text-muted-foreground">
                See what the five-day accelerated treatment experience looks
                like, from brain mapping to the final session.
              </p>
            </div>
            <div className="relative w-full overflow-hidden rounded-2xl border border-border shadow-premium bg-black">
              <YouTubeFacade
                id="pZRgDnrmMvI"
                title="The SAINT TMS Patient Journey"
              />
            </div>
          </div>
        </section>

        {/* 1b. Authority chain — Stanford → FDA → Magnus → Verified Provider → Neuro Wellness */}
        <AuthorityChain />

        {/* 2. What SAINT® is — 4 key differentiators (FDA-cleared, 5 days, fMRI, non-invasive) */}
        <Suspense fallback={null}>
          <TmsTherapy />
        </Suspense>

        {/* 2b. Key takeaways — quotable summary for AI answer engines */}
        <KeyTakeaways
          items={[
            {
              text: "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared accelerated TMS protocol that delivers 50 stimulation sessions over five consecutive days, compared to 30–36 sessions over 6–9 weeks for standard TMS.",
            },
            {
              text: "SAINT® uses each patient's own resting-state fMRI to personalize the stimulation target, rather than relying on population-average scalp measurements used in conventional TMS.",
            },
            {
              text: "In a double-blind, sham-controlled randomized trial, 79% of the active treatment group reached remission at four weeks versus 13% of the sham group (Cole et al., Am J Psychiatry, 2022). Individual results vary.",
            },
            {
              text: "SAINT® is FDA-cleared (510(k) K220177, September 2022) for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode.",
            },
            {
              text: "Neuro Wellness TMS Centers of America in Coral Springs, Florida is a Verified SAINT® Provider on Magnus Medical's official locator, serving South Florida and out-of-state patients.",
            },
          ]}
        />

        {/* 3. The research — remission rates, time to remission, 12-month durability */}
        <Suspense fallback={null}>
          <SaintOutcomes />
        </Suspense>

        {/* 4. Why personalized targeting matters — standard vs fMRI-guided */}
        <Suspense fallback={null}>
          <SaintTargetingComparisonSection />
        </Suspense>

        {/* 4b. SAINT explainer videos + full transcript */}
        <SaintVideoSection variant="full" />

        {/* 5. Your week — the 5-day step-by-step schedule + facility photos */}
        <Suspense fallback={null}>
          <SaintWeek />
        </Suspense>

        {/* 6. Is it right for you? — candidacy + FDA contraindications */}
        <Suspense fallback={null}>
          <SaintCandidacy onQuiz={() => setQuizOpen(true)} />
        </Suspense>

        {/* 7. Cost, insurance & two access pathways */}
        <Suspense fallback={null}>
          <SaintCost />
        </Suspense>
        <FreeBenefitsCheck />
        <Suspense fallback={null}>
          <TwoWaysToGetStarted
            heading="Two ways to access SAINT®"
            eyebrow="Access & Availability"
          />
        </Suspense>

        {/* 8. Patient stories + private suites experience */}
        <Suspense fallback={null}>
          <SaintStories />
        </Suspense>
        <Suspense fallback={null}>
          <SaintPrivateSuites />
        </Suspense>

        {/* 10. Why patients travel to us + travel planning */}
        <Suspense fallback={null}>
          <SaintWhyUs />
        </Suspense>
        <Suspense fallback={null}>
          <SaintTravelTeaser />
        </Suspense>

        {/* 11. Articles callout — points to the library at the bottom */}
        <section className="py-10 sm:py-12 bg-muted/20 border-y border-border">
          <div className="container-px mx-auto max-w-5xl px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl border border-accent/20 bg-card px-6 py-5 shadow-soft">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <span className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <BookOpen className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-headings text-lg font-bold text-primary leading-snug">
                    Want to go deeper? Explore our SAINT® clinical articles.
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Plain-language guides on treatment-resistant depression, how
                    SAINT® works, side effects, and what to expect.
                  </p>
                </div>
              </div>
              <a
                href="#saint-articles"
                className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
              >
                <span>Browse Articles</span>
                <ArrowDown className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* 12. FAQ + consultation CTA */}
        <Suspense fallback={null}>
          <SaintFaq />
        </Suspense>
        <Suspense fallback={null}>
          <SaintConsultationCta onQuiz={() => setQuizOpen(true)} />
        </Suspense>

        {/* 13. Closing + article library + disclaimer */}
        <Suspense fallback={null}>
          <BrighterTomorrowBanner />
        </Suspense>
        <Suspense fallback={null}>
          <SaintArticlesSection />
        </Suspense>

        {/* Patient-education deep-dive links to /learn articles */}
        <Suspense fallback={null}>
          <RelatedPages
            heading="SAINT® Patient Education: Go Deeper"
            links={[
              {
                title: "What Is SAINT® TMS?",
                href: "/learn/what-is-saint-tms",
                description:
                  "The 5-day, fMRI-guided treatment explained in plain language",
              },
              {
                title: "The SAINT® TMS Protocol, Step by Step",
                href: "/learn/saint-tms-protocol",
                description:
                  "Consultation, brain scan, targeting, and the 5-day treatment week",
              },
              {
                title: "How Does SAINT® TMS Work?",
                href: "/learn/how-does-saint-tms-work",
                description:
                  "How fMRI targeting and accelerated sessions treat depression",
              },
              {
                title: "SAINT® vs. Traditional TMS: What's Different?",
                href: "/learn/saint-vs-traditional-tms",
                description:
                  "Key differences in targeting, schedule, and time to remission",
              },
              {
                title: "Is SAINT® TMS Covered by Insurance?",
                href: "/learn/is-saint-tms-covered-by-insurance",
                description:
                  "Medicare, BCBS, commercial plans, and how prior authorization works",
              },
              {
                title: "SAINT® TMS Cost: What You're Paying For",
                href: "/learn/saint-tms-cost",
                description:
                  "What's included in the 5-day treatment and how to pay",
              },
              {
                title: "Who Qualifies for SAINT® TMS?",
                href: "/learn/who-qualifies-for-saint-tms",
                description:
                  "Eligibility, FDA criteria, and contraindications explained",
              },
              {
                title: "What to Expect During SAINT® TMS",
                href: "/learn/what-to-expect-during-saint-tms",
                description:
                  "Your consultation, scan, and the 5-day treatment experience",
              },
              {
                title: "Can SAINT® Help If Antidepressants Didn't Work?",
                href: "/learn/can-saint-help-if-antidepressants-didnt-work",
                description: "When medication fails and what comes next",
              },
              {
                title: "SAINT® TMS Near Me: How to Find It",
                href: "/learn/saint-tms-near-me",
                description:
                  "Verified providers and when traveling is worth it",
              },
              {
                title: "SAINT® vs. Spravato® for Depression",
                href: "/learn/saint-vs-spravato",
                description: "How two FDA-cleared TRD treatments compare",
              },
              {
                title:
                  "Stanford Accelerated Intelligent Neuromodulation Therapy, Explained",
                href: "/learn/stanford-accelerated-intelligent-neuromodulation-therapy",
                description:
                  "What each word means and how Stanford developed SAINT",
              },
              {
                title:
                  "SAINT® vs. Lower-Cost “Accelerated TMS”: Not the Same Treatment",
                href: "/learn/saint-vs-accelerated-tms",
                description:
                  "What's different about FDA clearance, targeting, evidence, and price",
              },
              {
                title: "Is It Really SAINT®? How to Verify a Clinic",
                href: "/learn/what-is-a-verified-saint-provider",
                description: "A 3-step check and red flags before you pay",
              },
              {
                title: "SAINT® TMS Results & Reviews",
                href: "/learn/saint-tms-results",
                description:
                  "Trial data (78.6% remission) and patient experiences",
              },
            ]}
          />
        </Suspense>
        <Suspense fallback={null}>
          <SaintDisclaimer />
        </Suspense>
        <div className="container-px mx-auto max-w-3xl px-4 py-8">
          <Suspense fallback={null}>
            <CrisisLifelineBanner />
          </Suspense>
        </div>
        <Suspense fallback={null}>
          <RelatedPages
            heading="Related Treatments & Resources"
            links={[
              {
                title: "How SAINT® Works",
                href: "/learn/how-does-saint-tms-work",
                description:
                  "A calm, plain-language walkthrough of the 5-day treatment journey",
              },
              {
                title: "Ampa One Day Protocol",
                href: "/ampa",
                description: "Single-day accelerated TMS treatment",
              },
              {
                title: "TMS for Depression",
                href: "/services/depression/",
                description: "Standard TMS for treatment-resistant depression",
              },
              {
                title: "SAINT vs. Standard TMS",
                href: "/learn/saint-vs-traditional-tms",
                description:
                  "Key differences in targeting, schedule, and duration",
              },
              {
                title: "Clinical Research Library",
                href: "/clinical-research",
                description:
                  "Peer-reviewed studies behind SAINT, TMS, and SPRAVATO",
              },
              {
                title: "Cost & Insurance",
                href: "/cost-insurance",
                description: "Financing options and insurance verification",
              },
              {
                title: "Am I a Candidate?",
                href: "/am-i-a-candidate",
                description: "Screening factors — not disqualifiers",
              },
              {
                title: "TMS Treatment in Coral Springs",
                href: "/tms-coral-springs-fl",
                description: "Our Coral Springs, FL location",
              },
            ]}
          />
        </Suspense>
        <Suspense fallback={null}>
          <Consultation />
        </Suspense>

        {/* 14. Final eligibility form — the page's last conversion target */}
        <section className="bg-muted/20 border-t border-border py-14 sm:py-16">
          <div className="container-px mx-auto max-w-2xl px-4">
            <SaintEligibilityCheck
              source="saint-end"
              variant="card"
              redirectTo="/thank-you-saint"
              heading="Still struggling after trying medication?"
              subheading="See if SAINT® is right for you — three quick questions, no obligation, a real answer from our clinical team."
            />
          </div>
        </section>
      </main>

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        widgetId="AnQv9vi7iCruuGA7ncnM"
      />
      <SaintQuizPopup open={quizOpen} onOpenChange={setQuizOpen} />

      <Footer />
    </div>
  );
};

export default Saint;
